import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/apiClient";

/** Workflow → AMC (annual maintenance contracts). */
export type AmcStatus = "DRAFT" | "PENDING_APPROVAL" | "ACTIVE" | "EXPIRED" | "CANCELLED";
export type AmcType = "COMPREHENSIVE" | "NON_COMPREHENSIVE" | "PREVENTIVE" | "ON_CALL" | "OTHER";
export type AmcAction = "submit" | "approve" | "reject" | "cancel" | "reopen";

export const AMC_STATUS_LABEL: Record<AmcStatus, string> = {
  DRAFT: "Draft",
  PENDING_APPROVAL: "Pending Approval",
  ACTIVE: "Active",
  EXPIRED: "Expired",
  CANCELLED: "Cancelled",
};
export const AMC_TYPE_LABEL: Record<AmcType, string> = {
  COMPREHENSIVE: "Comprehensive",
  NON_COMPREHENSIVE: "Non-comprehensive",
  PREVENTIVE: "Preventive maintenance",
  ON_CALL: "On-call",
  OTHER: "Other",
};

export interface AmcAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  uploadedByName: string;
  createdAt: string;
}

export interface Amc {
  id: string;
  amcId: string;
  projectName: string;
  boardId: string | null;
  customerId: string | null;
  customerName: string | null;
  amcType: AmcType;
  startDate: string | null;
  endDate: string | null;
  contractAmount: number | null;
  currency: string;
  assignedUserId: string | null;
  assignedName: string | null;
  vendorId: string | null;
  vendorName: string | null;
  status: AmcStatus;
  remarks: string | null;
  decisionNote: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
  attachments: AmcAttachment[];
  /** request 1100 */
  frequency: AmcFrequency | null;
  /** request 1127 */
  paymentTerm: AmcPaymentTerm | null;
  payments?: AmcPaymentRow[];
  nextServiceDate?: string | null;
  overdueVisits?: number;
  visits?: AmcVisit[];
  jobs?: AmcJob[];
  workLog?: AmcWorkLogEntry[];
  people?: AmcPersonRow[];
}

export interface AmcInput {
  projectName?: string;
  boardId?: string | null;
  customerId?: string | null;
  amcType?: AmcType;
  startDate?: string | null;
  endDate?: string | null;
  contractAmount?: number | null;
  currency?: string;
  assignedUserId?: string | null;
  vendorId?: string | null;
  remarks?: string | null;
  frequency?: AmcFrequency | null;
  paymentTerm?: AmcPaymentTerm | null;
}

export interface AmcList {
  rows: Amc[];
  // request 1123: null (and canSeeAmounts false) for people without "See contract amounts"
  summary: { total: number; byStatus: Record<AmcStatus, number>; activeValueAed: number | null; expiringSoon: number };
  canSeeAmounts?: boolean;
}

export interface AmcLookups {
  projects: Array<{ id: string; boardId: string; name: string; customerId: string | null }>;
  customers: Array<{ id: string; name: string }>;
  users: Array<{ id: string; name: string }>;
  vendors: Array<{ id: string; name: string }>;
  employees: AmcEmployeeChoice[];
}

type Envelope<T> = { data: T };

export const useAmcs = (f: { search?: string; status?: string; amcType?: string; expiring?: string; frequency?: string; service?: string }) =>
  useQuery({ queryKey: ["amc", "list", f], queryFn: async () => (await api.get<Envelope<AmcList>>("/amc", { params: f })).data.data });

export const useAmcLookups = (enabled = true) =>
  useQuery({ queryKey: ["amc", "lookups"], queryFn: async () => (await api.get<Envelope<AmcLookups>>("/amc/lookups")).data.data, enabled, staleTime: 60_000 });

function useAmcMutation<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: ["amc"] }) });
}

export const useCreateAmc = () => useAmcMutation(async (input: AmcInput) => (await api.post<Envelope<Amc>>("/amc", input)).data.data);
export const useUpdateAmc = () => useAmcMutation(async (v: { id: string; input: AmcInput }) => (await api.patch<Envelope<Amc>>(`/amc/${v.id}`, v.input)).data.data);
export const useAmcStatus = () => useAmcMutation(async (v: { id: string; action: AmcAction; note?: string }) => (await api.post<Envelope<Amc>>(`/amc/${v.id}/status`, { action: v.action, note: v.note })).data.data);
export const useDeleteAmc = () => useAmcMutation(async (id: string) => api.delete(`/amc/${id}`));
export const useUploadAmcFiles = () =>
  useAmcMutation(async (v: { id: string; files: File[] }) => {
    const fd = new FormData();
    v.files.forEach((f) => fd.append("files", f));
    return (await api.post<Envelope<Amc>>(`/amc/${v.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data.data;
  });
export const useDeleteAmcFile = () => useAmcMutation(async (attachmentId: string) => api.delete(`/amc/attachments/${attachmentId}`));

export async function downloadAmcFile(attachmentId: string, fileName: string) {
  const res = await api.get(`/amc/attachments/${attachmentId}/download`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export const useAmc = (id: string | undefined) =>
  useQuery({ queryKey: ["amc", "one", id], queryFn: async () => (await api.get<Envelope<Amc>>(`/amc/${id}`)).data.data, enabled: !!id });

// ------------------------------------------------------------------------------------------------ request 1100
/** AMC maintenance schedule — visits generated from the frequency, plus assigned employees and emergency contacts. */
export type AmcFrequency = "MONTHLY" | "QUARTERLY" | "HALF_YEARLY";
export const AMC_FREQUENCY_LABEL: Record<AmcFrequency, string> = { MONTHLY: "Monthly", QUARTERLY: "Quarterly", HALF_YEARLY: "Half-Yearly" };
export type AmcVisitStatus = "UPCOMING" | "IN_PROGRESS" | "COMPLETED" | "SKIPPED";
/** request 1127: how the client pays */
export type AmcPaymentTerm = "MONTHLY" | "QUARTERLY" | "HALF_YEARLY" | "ANNUALLY" | "ONE_TIME";
export const PAYMENT_TERM_LABEL: Record<AmcPaymentTerm, string> = { MONTHLY: "Monthly", QUARTERLY: "Quarterly", HALF_YEARLY: "Half-yearly", ANNUALLY: "Annually", ONE_TIME: "One-time" };
export interface AmcPaymentRow {
  id: string | null;
  termNo: number;
  periodStart: string;
  periodEnd: string;
  amount: number | null;
  currency: string;
  status: "PENDING" | "DUE" | "PAID";
  notifiedAt: string | null;
  paidAt: string | null;
  paidByName: string | null;
  reference: string | null;
  note: string | null;
}
export type AmcVisitState = AmcVisitStatus | "OVERDUE";
export const VISIT_STATE_LABEL: Record<AmcVisitState, string> = { UPCOMING: "Upcoming", IN_PROGRESS: "In progress", COMPLETED: "Completed", SKIPPED: "Skipped", OVERDUE: "Overdue" };
export interface AmcVisit {
  id: string;
  amcId: string;
  dueDate: string;
  status: AmcVisitStatus;
  state: AmcVisitState;
  /** request 1303: the last day of its period; it's overdue after this */
  lastDay: string;
  isManual: boolean;
  assignedUserId: string | null;
  assignedName: string | null;
  startedAt: string | null;
  completedAt: string | null;
  completedByName: string | null;
  notes: string | null;
  /** request 1128: service report / photos */
  files?: AmcAttachment[];
}
export interface AmcPersonRow {
  id: string;
  role: "ASSIGNED" | "EMERGENCY";
  altPhone: string | null;
  note: string | null;
  employee: { id: string; employeeCode: string; fullName: string; jobTitle: string | null; phone: string | null; department: { name: string } | null };
}
export interface AmcEmployeeChoice {
  id: string;
  employeeCode: string;
  fullName: string;
  jobTitle: string | null;
  phone: string | null;
  department: { name: string } | null;
}
export interface AmcScheduleRow extends AmcVisit {
  amc: { id: string; amcId: string; projectName: string; customerName: string | null; frequency: AmcFrequency | null; status: AmcStatus; boardId: string | null };
}
export interface AmcSchedule {
  items: AmcScheduleRow[];
  summary: { total: number; upcoming: number; inProgress: number; completed: number; overdue: number; skipped: number };
}
export const useAmcSchedule = (f: Record<string, string | undefined>) =>
  useQuery({ queryKey: ["amc", "schedule", f], queryFn: async () => (await api.get<Envelope<AmcSchedule>>("/amc/schedule", { params: Object.fromEntries(Object.entries(f).filter(([, v]) => v)) })).data.data });
export const useAddAmcVisit = () => useAmcMutation(async (v: { amcId: string; dueDate: string; assignedUserId?: string | null; notes?: string }) => (await api.post(`/amc/${v.amcId}/visits`, v)).data.data);
export const useUpdateAmcVisit = () =>
  useAmcMutation(async (v: { id: string; status?: AmcVisitStatus; dueDate?: string; assignedUserId?: string | null; notes?: string | null }) => (await api.patch(`/amc/visits/${v.id}`, v)).data.data);
export const useDeleteAmcVisit = () => useAmcMutation(async (id: string) => api.delete(`/amc/visits/${id}`));
/** request 1128: files for a visit (needed to complete it) */
export const useUploadVisitFiles = () =>
  useAmcMutation(async (v: { visitId: string; files: File[] }) => {
    const fd = new FormData();
    v.files.forEach((f) => fd.append("files", f));
    return (await api.post(`/amc/visits/${v.visitId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data.data;
  });
/** request 1127: Accounts mark a payment term paid (or back to due) */
export const useUpdateAmcPayment = () =>
  useAmcMutation(async ({ id, ...body }: { id: string; status: "PAID" | "DUE"; paidDate?: string; reference?: string; note?: string }) => (await api.patch(`/amc/payments/${id}`, body)).data.data);
export const useAddAmcPerson = () =>
  useAmcMutation(async (v: { amcId: string; employeeId: string; role: "ASSIGNED" | "EMERGENCY"; altPhone?: string; note?: string }) => (await api.post(`/amc/${v.amcId}/people`, v)).data.data);
export const useUpdateAmcPerson = () => useAmcMutation(async (v: { id: string; altPhone?: string | null; note?: string | null }) => (await api.patch(`/amc/people/${v.id}`, v)).data.data);
export const useRemoveAmcPerson = () => useAmcMutation(async (id: string) => api.delete(`/amc/people/${id}`));

// ------------------------------------------------------------------------------------------------ request 1104
/** AMC emergency work (allocated to employees) and the work record (every piece of work done on the AMC). */
export type AmcJobStatus = "OPEN" | "IN_PROGRESS" | "COMPLETED" | "CANCELLED";
export type AmcJobPriority = "NORMAL" | "URGENT" | "CRITICAL";
export const JOB_STATUS_LABEL: Record<AmcJobStatus, string> = { OPEN: "Open", IN_PROGRESS: "In progress", COMPLETED: "Completed", CANCELLED: "Cancelled" };
export const JOB_PRIORITY_LABEL: Record<AmcJobPriority, string> = { NORMAL: "Normal", URGENT: "Urgent", CRITICAL: "Critical" };
export interface AmcJob {
  id: string;
  jobNo: string;
  amcId: string;
  title: string;
  description: string | null;
  location: string | null;
  priority: AmcJobPriority;
  status: AmcJobStatus;
  reportedAt: string;
  dueBy: string | null;
  startedAt: string | null;
  completedAt: string | null;
  completedByName: string | null;
  resolution: string | null;
  hoursSpent: number | null;
  createdByName: string;
  assignees: Array<{ userId: string; name: string }>;
  amc?: { id: string; amcId: string; projectName: string; customerName: string | null };
  canManage?: boolean;
  isMine?: boolean;
}
export interface AmcWorkLogEntry {
  id: string;
  workDate: string;
  source: "VISIT" | "EMERGENCY" | "MANUAL";
  sourceId: string | null;
  title: string;
  details: string | null;
  doneBy: string | null;
  hours: number | null;
  createdByName: string;
  createdAt: string;
}
export const useCreateAmcJob = () =>
  useAmcMutation(async (v: { amcId: string; title: string; description?: string; location?: string; priority: AmcJobPriority; dueBy?: string | null; assigneeUserIds: string[] }) => (await api.post<Envelope<AmcJob>>(`/amc/${v.amcId}/jobs`, v)).data.data);
export const useUpdateAmcJob = () => useAmcMutation(async (v: { id: string } & Partial<{ title: string; description: string; location: string; priority: AmcJobPriority; dueBy: string | null; assigneeUserIds: string[] }>) => (await api.patch<Envelope<AmcJob>>(`/amc/jobs/${v.id}`, v)).data.data);
export const useDeleteAmcJob = () => useAmcMutation(async (id: string) => api.delete(`/amc/jobs/${id}`));
export function useAmcJobStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; status: AmcJobStatus; resolution?: string; hoursSpent?: number | null }) => (await api.post<Envelope<AmcJob>>(`/amc-jobs/${v.id}/status`, v)).data.data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["amc"] });
      qc.invalidateQueries({ queryKey: ["amc-jobs"] });
    },
  });
}
export const useMyAmcJobs = (scope: "mine" | "all" = "mine") =>
  useQuery({ queryKey: ["amc-jobs", "mine", scope], queryFn: async () => (await api.get<Envelope<{ jobs: AmcJob[]; canManage: boolean; openMine: number }>>("/amc-jobs/mine", { params: { scope } })).data.data, refetchInterval: 60_000 });
export const useAddAmcWorkLog = () =>
  useAmcMutation(async (v: { amcId: string; workDate: string; title: string; details?: string; doneBy?: string; hours?: number | null }) => (await api.post(`/amc/${v.amcId}/work-log`, v)).data.data);
export const useDeleteAmcWorkLog = () => useAmcMutation(async (id: string) => api.delete(`/amc/work-log/${id}`));
